import { useEffect, useState } from "react"
import { Button, Col, Row, Modal, Spinner, Badge, Card } from "react-bootstrap"
import { AnimationOnScroll } from 'react-animation-on-scroll'
import { useNavigate } from 'react-router-dom'
import http from "../helpers/Request"
import EventRegistrationForm from "./EventRegistrationForm"
import moment from 'moment'
import { formatDateTime, stripHtml, truncateText } from "../helpers/BlogText"
import ScrollToTopOnMount from "../helpers/ScrollToTopOnMount"

function EventList({ showTitle = true, limit = null, type = 'active' }) {
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showModal, setShowModal] = useState(false)

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const endpoint = type === 'upcoming-closed' ? '/api/events/upcoming-closed' : '/api/events/active'
      const response = await http.get(endpoint)
      if (response.data.status) {
        setEvents(response.data.data)
      } else {
        setError('Failed to load events')
      }
    } catch (err) {
      console.error('Error fetching events:', err)
      setError('Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  const handleRegisterClick = (event, e) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedEvent(event)
    setShowModal(true)
  }

  const handleEventClick = (event) => {
    navigate(`/events/${event.slug}`)
  }

  const handleViewDetails = (event, e) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/events/${event.slug}`)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedEvent(null)
  }

  const handleRegistrationSuccess = () => {
    handleCloseModal()
    // Refresh events to update available slots
    fetchEvents()
  }

  const getCountdownText = (deadline) => {
    const now = moment()
    const deadlineDate = moment(deadline)
    const duration = moment.duration(deadlineDate.diff(now))
    
    if (duration.asMilliseconds() <= 0) {
      return 'Registration Closed'
    }
    
    const days = Math.floor(duration.asDays())
    const hours = duration.hours()
    const minutes = duration.minutes()
    
    if (days > 0) {
      return `${days} days, ${hours}h left`
    } else if (hours > 0) {
      return `${hours}h ${minutes}m left`
    } else {
      return `${minutes}m left`
    }
  }

  const isRegistrationClosed = (deadline) => {
    return moment().isAfter(moment(deadline))
  }

  const getEventTypeBadge = (type) => {
    return type === 'online' ? (
      <Badge bg="primary" className="me-2">
        <i className="fas fa-video me-1"></i> Online
      </Badge>
    ) : (
      <Badge bg="success" className="me-2">
        <i className="fas fa-map-marker-alt me-1"></i> Offline
      </Badge>
    )
  }

  const getAvailabilityText = (event) => {
    if (event.max_participants === null) {
      return 'Unlimited slots'
    }
    
    const available = event.available_slots
    if (available <= 0) {
      return 'Fully Booked'
    }
    
    return `${available} slots available`
  }

  const isFullyBooked = (event) => {
    return event.max_participants !== null && event.available_slots <= 0
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading events...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger">
          {error}
        </div>
        <Button variant="outline-primary" onClick={fetchEvents}>
          Try Again
        </Button>
      </div>
    )
  }

  if (events.length === 0) {
    if(type === 'upcoming-closed'){
      return <></>
    }else{
      return (
        <div className="text-center py-5">
          <h4>No Events Available</h4>
          <p className="text-muted">Check back later for upcoming events!</p>
        </div>
      )
    }
  }

  return (
    <>
      <ScrollToTopOnMount/>
      <div className="events-section">
        {showTitle && (
          <AnimationOnScroll animateOnce={true} animateIn="animate__fadeIn" duration={.8} animatePreScroll={true} offset={100}>
            <div className='header text-center'>
              {type === 'upcoming-closed' ? 'Upcoming ' : 'Siberwander '}
              <span className="text-brand-secondary">
                {type === 'upcoming-closed' ? 'Events' : 'Events'}
              </span>
            </div>
            <p className='mt-4 text-justify text-center'>
              <strong>
                {type === 'upcoming-closed'
                  ? 'Registration Closed - The Event Will Take Place'
                  : 'Join Our Exclusive Events'
                }
              </strong>
            </p>
            <p className='text-justify text-center'>
              {type === 'upcoming-closed'
                ? 'Registration for these events has closed, but you can still view event details and information. Check back for future events with open registration!'
                : 'Stay ahead with the latest insights in cybersecurity and cloud security. Register now for our upcoming events!'
              }
            </p>
          </AnimationOnScroll>
        )}

        <Row className={showTitle ? "mt-5" : ""}>
          {(limit ? events.slice(0, limit) : events).map((event, index) => (
            <Col lg={4} md={6} xs={12} key={event.id} className="mb-4">
              <AnimationOnScroll 
                animateOnce={true} 
                animateIn="animate__slideInUp" 
                duration={.8} 
                delay={index * 100} 
                animatePreScroll={true} 
                offset={100}
                style={{height: "100%"}}
              >
                <Card
                  className="h-100 card-shadow event-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleEventClick(event)}
                >
                  {event.image && (
                    <Card.Img 
                      variant="top" 
                      src={event.image} 
                      style={{height: '200px', objectFit: 'cover'}}
                      onError={(e) => {e.target.style.display = 'none'}}
                    />
                  )}
                  <Card.Body className="d-flex flex-column">
                    <div className="mb-3">
                      {getEventTypeBadge(event.event_type)}
                      <Badge
                        bg={type === 'upcoming-closed' ? 'secondary' : (isRegistrationClosed(event.registration_deadline) ? 'danger' : 'warning')}
                        text="dark"
                      >
                        {type === 'upcoming-closed'
                          ? 'Registration Closed'
                          : getCountdownText(event.registration_deadline)
                        }
                      </Badge>
                    </div>
                    
                    <Card.Title className="event-title">
                      {event.title}
                    </Card.Title>
                    
                    <div className="event-details mb-3 mt-3">
                      <div className="">
                        <i className="fas fa-calendar-alt text-brand"></i>
                        <strong>Event Date:</strong> {formatDateTime(event.event_date)}
                      </div>
                      
                      {event.event_type === 'offline' && event.event_location && (
                        <div className="">
                          <i className="fas fa-map-marker-alt text-brand"></i>
                          <strong>Location:</strong> {event.event_location}
                        </div>
                      )}
                      
                      <div className="mb-2">
                        <i className="fas fa-users text-brand"></i>
                        <strong>Availability:</strong> {getAvailabilityText(event)}
                      </div>
                    </div>
                    
                    <div className="mt-auto">
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-fill"
                          onClick={(e) => handleViewDetails(event, e)}
                        >
                          View Details
                        </Button>
                        <Button
                          variant={type === 'upcoming-closed' ? 'secondary' : 'brand'}
                          size="sm"
                          className="flex-fill"
                          onClick={(e) => type === 'upcoming-closed' ? null : handleRegisterClick(event, e)}
                          disabled={type === 'upcoming-closed' || isRegistrationClosed(event.registration_deadline) || isFullyBooked(event)}
                        >
                          {type === 'upcoming-closed'
                            ? 'Registration Closed'
                            : isRegistrationClosed(event.registration_deadline)
                            ? 'Closed'
                            : isFullyBooked(event)
                            ? 'Booked'
                            : 'Register'
                          }
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </AnimationOnScroll>
            </Col>
          ))}
        </Row>
      </div>

      {/* Registration Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Register for Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <EventRegistrationForm 
              event={selectedEvent}
              onSuccess={handleRegistrationSuccess}
              onCancel={handleCloseModal}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default EventList